import { useEffect } from 'react';

/**
 * useAutoFocusFirstEmpty — Phase 13 Processing Fluency.
 *
 * Focuses the first empty, required, focusable element inside the
 * referenced container on mount (or when `trigger` changes). Scrolls the
 * element into view if it lies outside the viewport.
 *
 * Respects disabled / readonly fields. Safe on SSR. Runs exactly once per
 * trigger change.
 *
 * Usage:
 *   const ref = useRef(null);
 *   useAutoFocusFirstEmpty(ref);
 *   return <form ref={ref}>...</form>;
 */
const SELECTOR = [
  'input[required]:not([disabled]):not([readonly])',
  'select[required]:not([disabled])',
  'textarea[required]:not([disabled]):not([readonly])',
].join(',');

function isEmpty(el) {
  if (!el) return false;
  if (el.tagName === 'SELECT') return !el.value;
  const v = el.value;
  if (typeof v !== 'string') return !v;
  return v.trim() === '';
}

export function useAutoFocusFirstEmpty(containerRef, trigger = true) {
  useEffect(() => {
    if (!trigger) return;
    if (typeof window === 'undefined') return;
    const root = containerRef?.current;
    if (!root) return;
    // Defer to next tick so child portals / conditional renders settle.
    const t = window.setTimeout(() => {
      const candidates = Array.from(root.querySelectorAll(SELECTOR));
      const first = candidates.find(isEmpty);
      if (!first) return;
      try {
        first.focus({ preventScroll: true });
        first.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      } catch {
        /* ignore */
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [containerRef, trigger]);
}

export default useAutoFocusFirstEmpty;
